import type { editor } from "monaco-editor";

import { Theme } from "@/services/monday";

const baseRules = [
  {
    background: "FFFFFF",
    token: "",
  },
  {
    token: "invalid",
    foreground: "c82829",
  },
  {
    foreground: "8e908c",
    token: "comment",
  },
  {
    token: "escape",
    foreground: "f5871f",
  },
  {
    token: "site-or-client",
    foreground: "8959a8",
  },
  {
    token: "client",
    foreground: "666969",
  },
  {
    token: "param",
    foreground: "008080",
  },
  {
    token: "action-param",
    foreground: "8959a8",
    bold: true,
  },
  {
    token: "action-value",
    foreground: "4271ae",
    bold: true,
  },
  {
    token: "null",
    foreground: "666969",
  },
  {
    foreground: "c99e00",
    token: "regexp",
  },
  {
    token: "var-repl",
    foreground: "666969",
  },
  {
    token: "var-special",
    foreground: "f5871f",
  },
  {
    token: "string",
    foreground: "718c00",
  },
  {
    token: "punctuation",
    foreground: "f5871f",
  },
];

const light_theme = {
  base: "vs",
  inherit: true,
  rules: baseRules,
  colors: {
    "editor.foreground": "#4D4D4C",
    "editor.background": "#FFFFFF",
    "editor.selectionBackground": "#D6D6D6",
    // "editor.lineHighlightBackground": "#EFEFEF",
    "editor.lineHighlightBorder": "#FFFFFF",
    "editorCursor.foreground": "#AEAFAD",
    "editorWhitespace.foreground": "#D1D1D1",
  },
} as editor.IStandaloneThemeData;

const dark_theme = {
  base: "vs-dark",
  inherit: true,
  rules: baseRules,
  colors: {
    // Get css var(--color-text-primary) from monday.com with javascript:
    // document.querySelector("body").style.getPropertyValue("--primary-text-color")
    "editor.foreground": "#d5d8df",
    "editor.background": "#181b34",
    "editor.selectionBackground": "#D6D6D6",
    // "editor.lineHighlightBackground": "#EFEFEF",
    "editor.lineHighlightBorder": "#FFFFFF",
    "editorCursor.foreground": "#AEAFAD",
    "editorWhitespace.foreground": "#D1D1D1",
  },
} as editor.IStandaloneThemeData;

const black_theme = {
  base: "vs-dark",
  inherit: true,
  rules: baseRules,
  colors: {
    "editor.foreground": "#4D4D4C",
    "editor.background": "#FFFFFF",
    "editor.selectionBackground": "#D6D6D6",
    // "editor.lineHighlightBackground": "#EFEFEF",
    "editor.lineHighlightBorder": "#FFFFFF",
    "editorCursor.foreground": "#AEAFAD",
    "editorWhitespace.foreground": "#D1D1D1",
  },
} as editor.IStandaloneThemeData;

const hacker_theme = {
  base: "vs-dark",
  inherit: true,
  rules: baseRules,
  colors: {
    "editor.foreground": "#ffffff",
    "editor.background": "#282a36",
    "editor.selectionBackground": "#D6D6D6",
    // "editor.lineHighlightBackground": "#EFEFEF",
    "editor.lineHighlightBorder": "#aa0000",
    "editorCursor.foreground": "#AEAFAD",
    "editorWhitespace.foreground": "#D1D1D1",
  },
} as editor.IStandaloneThemeData;

// Sadly we have to give a name without underscores to Monaco themes but monday themes can contain underscores.
export type NamedTheme = {
  name: string;
  data: editor.IStandaloneThemeData;
};

const themes = {
  [Theme.LIGHT]: { name: "light", data: light_theme },
  [Theme.DARK]: { name: "dark", data: dark_theme },
  [Theme.BLACK]: { name: "black", data: black_theme },
  [Theme.HACKER]: { name: "hacker", data: hacker_theme },
};

export function getMonacoThemeName(mondayTheme: Theme): string {
  const t = themes[mondayTheme] || themes[Theme.LIGHT];
  return t.name;
}

export function getMonacoThemeData(mondayTheme: Theme): NamedTheme {
  const t = themes[mondayTheme] || themes[Theme.LIGHT];

  const bodySelector = document.querySelector("body") as HTMLElement;
  const styles = getComputedStyle(bodySelector);
  const textColor = styles.getPropertyValue("--primary-text-color");
  const backgroundColor = styles.getPropertyValue("--primary-background-color");
  const selectionColor = styles.getPropertyValue("--primary-selected-color");

  if (textColor && backgroundColor && selectionColor) {
    t.data.colors = {
      ...t.data.colors,
      "editor.foreground": textColor,
      "editor.background": backgroundColor,
      "editor.selectionBackground": selectionColor,
      "editor.lineHighlightBorder": backgroundColor,
    };
  }
  return t;
}
